import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "./auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCK_MESSAGE = "Temporarily unavailable, please try after some time";

const FeaturesContext = createContext({
  features: {},
  loading: true,
  isLocked: () => false,
  isTabLocked: () => false,
  isTabHidden: () => false,
  lockMessage: LOCK_MESSAGE,
});

export function FeaturesProvider({ children }) {
  const { user } = useAuth();
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/social/config`)
      .then((r) => {
        const f = r.data.features || {};
        f.leaderboard_visible = f.global_leaderboard_enabled || f.job_offer_board_enabled;
        setFeatures(f);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const isAdmin = user?.role === "admin";
  // Capability-flag semantics: locked when flag is explicitly false. Admins bypass.
  const isLocked = (flag) => !!flag && features[flag] === false && !isAdmin;
  // Per-tab lock semantics: locked when flag is explicitly true. Admins bypass.
  const isTabLocked = (lockKey) => !!lockKey && features[lockKey] === true && !isAdmin;
  // Per-tab HIDE semantics: hidden when flag is explicitly true. Admins bypass. Dashboard is
  // never hideable (it's the redirect target — hiding it would loop).
  const isTabHidden = (hideKey) => !!hideKey && hideKey !== "dashboard_hidden" && features[hideKey] === true && !isAdmin;

  return (
    <FeaturesContext.Provider value={{ features, loading, isLocked, isTabLocked, isTabHidden, lockMessage: LOCK_MESSAGE }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export const useFeatures = () => useContext(FeaturesContext);
export const LOCK_TOAST = LOCK_MESSAGE;

function LockedPlaceholder() {
  return (
    <div
      className="page-container fade-in"
      data-testid="feature-locked-view"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", minHeight: "60vh", gap: "1rem" }}
    >
      <div style={{ width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(245,158,11,0.12)", color: "#f59e0b" }}>
        <Lock size={28} />
      </div>
      <h2 style={{ margin: 0 }}>Temporarily unavailable</h2>
      <p style={{ color: "var(--text-secondary)", maxWidth: 420 }}>{LOCK_MESSAGE}.</p>
    </div>
  );
}

/** Route guard: if the feature is locked for this user, show a popup + a locked placeholder.
 *  Accepts either a capability `flag` (enable semantics) or a per-tab `lockKey` (lock semantics). */
export function FeatureGate({ flag, lockKey, hideKey, children }) {
  const { isLocked, isTabLocked, isTabHidden, loading } = useFeatures();
  // Derive the hide key from the lock key when not passed explicitly (every gated route
  // already passes a lockKey), so hiding needs no per-route wiring in App.js.
  const effHideKey = hideKey || (lockKey ? lockKey.replace("_locked", "_hidden") : null);
  const hidden = isTabHidden(effHideKey);
  const locked = isLocked(flag) || isTabLocked(lockKey);

  useEffect(() => {
    if (!loading && locked && !hidden) {
      toast.error(LOCK_MESSAGE);
    }
  }, [loading, locked, hidden]);

  if (loading) return null;
  if (hidden) return <Navigate to="/app" replace />;
  if (locked) return <LockedPlaceholder />;
  return children;
}
